/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Alert, Empty, Typography, Spin, Input } from 'antd';
import { Fragment, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useTemplateArchiveFolderStructure } from '~src/hooks/api-hooks/useProjectBuilderModal';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { arrToTree, TableEntry } from '~src/utils/securityGroupsConfig';
import { ReviewFolderTree } from '../components/ReviewFolderTree';
import IconButton from '~components/_common/IconButton/IconButton';

const { Title } = Typography;

interface ReviewSelectionStepProps {
  projectId: number;
  templateId: string | null;
  selectedFolderIds: Set<number>;
  onRemoveFolder: (folderId: number) => void;
}

const filterFolderTree = (
  folders: TableEntry[],
  searchTerm: string
): TableEntry[] => {
  if (!searchTerm) return folders;

  const lowerSearch = searchTerm.toLowerCase();

  const filterRecursive = (folder: TableEntry): TableEntry | null => {
    const nameMatch = folder.title.toLowerCase().includes(lowerSearch);
    const descMatch = folder.dbFolder.description
      ?.toLowerCase()
      .includes(lowerSearch);

    let filteredChildren: TableEntry[] = [];
    if (folder.children) {
      filteredChildren = folder.children
        .map((child) => filterRecursive(child))
        .filter((child): child is TableEntry => child !== null);
    }

    if (nameMatch || descMatch || filteredChildren.length > 0) {
      return {
        ...folder,
        children:
          filteredChildren.length > 0 ? filteredChildren : folder.children,
      };
    }

    return null;
  };

  return folders
    .map((folder) => filterRecursive(folder))
    .filter((folder): folder is TableEntry => folder !== null);
};

export const ReviewSelectionStep: React.FC<ReviewSelectionStepProps> = ({
  projectId,
  templateId,
  selectedFolderIds,
  onRemoveFolder,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const [searchText, setSearchText] = useState('');

  const templateFoldersQuery = useTemplateArchiveFolderStructure(
    projectId,
    templateId,
    !!templateId
  );

  const selectedFolders = useMemo(() => {
    return (templateFoldersQuery.data || []).filter((folder) =>
      selectedFolderIds.has(folder.id)
    );
  }, [templateFoldersQuery.data, selectedFolderIds]);

  const folderTree = useMemo<TableEntry[]>(() => {
    const sortedFolders = [...selectedFolders].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    return arrToTree(sortedFolders, null);
  }, [selectedFolders]);

  const filteredFolderTree = useMemo(() => {
    return filterFolderTree(folderTree, searchText);
  }, [folderTree, searchText]);

  if (templateFoldersQuery.isLoading) {
    return (
      <div css={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <Alert
        message={intl.formatMessage({ id: 'review-selection-description' })}
        type="info"
        showIcon
        css={styles.alert}
      />

      <div css={styles.folderList}>
        <Title level={4} css={styles.sectionTitle}>
          {intl.formatMessage({ id: 'selected-folders-to-import' })} (
          {selectedFolders.length})
        </Title>

        {selectedFolders.length === 0 ? (
          <Empty
            description={intl.formatMessage({ id: 'no-folders-selected' })}
            css={styles.empty}
          />
        ) : (
          <Fragment>
            <Input
              prefix={
                <IconButton iconStyle="far" icon="search" backgroundOff />
              }
              size="small"
              placeholder={intl.formatMessage({ id: 'search-folders' })}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              css={styles.searchInput}
            />

            {filteredFolderTree.length === 0 ? (
              <Empty
                description={intl.formatMessage({
                  id: 'no-folders-match-search',
                })}
                css={styles.emptySearch}
              />
            ) : (
              <ReviewFolderTree
                folders={filteredFolderTree}
                selectedFolderIds={selectedFolderIds}
                onRemove={onRemoveFolder}
              />
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  alert: {
    marginBottom: 16,
  },
  folderList: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 16,
    color: theme.colors.text.primary,
    fontWeight: 600,
  },
  searchInput: {
    marginBottom: 16,
    '.ant-input': {
      backgroundColor: theme.colors.backgroundSecondary,
    },
  },
  empty: {
    padding: '60px 0',
  },
  emptySearch: {
    padding: '40px 0',
  },
});
